import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  Toolbar,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { useCompanyStore } from '@/stores/company-store';
import { useSnackbarStore } from '@certara/common-react-components';
import { apiClient } from '@/lib/api-client';

interface StatusDialogProps {
  open: boolean;
  onClose: () => void;
  logId?: number; // API expects numeric log ID
}

interface StatusLogResponse {
  Id?: number;
  Type?: string;
  SourceAppId?: string;
  SourceSubId?: string;
  ApplicationType?: string;
  Status?: string;
  Path?: string;
  Processed?: string;
  ErrorMessage?: string;
  ErrorStack?: string;
  UserId?: string;
}

interface StatusLogDetail {
  SourceAppId?: string;
  SourceSubId?: string;
  Path?: string;
  FormattedProcessedDate?: string;
  ErrorMessage?: string;
  ErrorStack?: string;
}

/**
 * Format the processed date from the backend format (e.g., "/Date(1234567890000)/")
 * to a human-readable string
 */
function formatProcessedDate(processed?: string): string {
  if (!processed) return '';

  // Handle .NET JSON date format: /Date(1234567890000)/ - use specific pattern
  const dateMatch = processed.match(/\/Date\((\d+)\)\//);
  // If specific pattern doesn't match, fall back to extracting digits for other formats
  const timestamp = dateMatch ? dateMatch[1] : processed.match(/^(\d+)$/)?.[1];
  if (timestamp) {
    const date = new Date(parseInt(timestamp, 10));
    return new Intl.DateTimeFormat('en-us', {
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true,
    }).format(date);
  }

  // Fallback: try to parse as ISO date
  const date = new Date(processed);
  if (!isNaN(date.getTime())) {
    return date.toLocaleString();
  }

  return processed;
}

export function StatusDialog({ open, onClose, logId }: StatusDialogProps) {
  const { activeCompany } = useCompanyStore();
  const { addSnackbar } = useSnackbarStore();
  const [logDetail, setLogDetail] = useState<StatusLogDetail>({});
  const [loading, setLoading] = useState(false);

  // Load log details when logId changes
  useEffect(() => {
    const loadLogDetails = async () => {
      if (!activeCompany || !logId) return;

      setLoading(true);
      try {
        // Call the actual API endpoint to get log details
        const response = await apiClient.get<StatusLogResponse>(
          `api/Status/GetLogDetails?companyId=${activeCompany.Id}&logId=${logId}`
        );

        if (response) {
          setLogDetail({
            SourceAppId: response.SourceAppId || '',
            SourceSubId: response.SourceSubId || '',
            Path: response.Path || '',
            FormattedProcessedDate: formatProcessedDate(response.Processed),
            ErrorMessage: response.ErrorMessage || '',
            ErrorStack: response.ErrorStack || '',
          });
        }
      } catch (error) {
        addSnackbar(
          'Failed to load log details',
          'error',
          error instanceof Error ? error.message : String(error)
        );
      } finally {
        setLoading(false);
      }
    };

    if (logId && activeCompany && open) {
      loadLogDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addSnackbar is stable from Zustand
  }, [logId, activeCompany, open]);

  const handleCopyContent = () => {
    const content =
      `Application Number\n${logDetail.SourceAppId || ''}\n\n` +
      `Sequence\n${logDetail.SourceSubId || ''}\n\n` +
      `Path\n${logDetail.Path || ''}\n\n` +
      `Processed Date\n${logDetail.FormattedProcessedDate || ''}\n\n` +
      `Error Message\n${logDetail.ErrorMessage || ''}\n\n` +
      `Error Stack\n${logDetail.ErrorStack || ''}`;

    try {
      navigator.clipboard.writeText(content);
      addSnackbar('Copied to clipboard', 'success');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        addSnackbar('Copied to clipboard', 'success');
      } catch (fallbackError) {
        addSnackbar('Copy failed. Please try again.', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Dialog
      data-testid="status-dialog"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <Toolbar
        sx={{ backgroundColor: 'primary.main', color: 'white' }}
        data-testid="status-dialog-toolbar"
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }} data-testid="status-dialog-title">
          Error Information
        </Typography>
        <IconButton
          data-testid="status-dialog-copy-button"
          color="inherit"
          onClick={handleCopyContent}
          sx={{ mr: 1 }}
          title="Copy to clipboard"
        >
          <CopyIcon />
        </IconButton>
        <IconButton
          data-testid="status-dialog-close-icon-button"
          color="inherit"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <DialogContent sx={{ p: 0 }} data-testid="status-dialog-content">
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }} data-testid="status-dialog-loading">
            <Typography>Loading log details...</Typography>
          </Box>
        ) : (
          <List data-testid="status-dialog-details-list">
            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Application Number
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', fontSize: '1rem' }}
                  >
                    {logDetail.SourceAppId || 'N/A'}
                  </Typography>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Sequence
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', fontSize: '1rem' }}
                  >
                    {logDetail.SourceSubId || 'N/A'}
                  </Typography>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Path
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', fontSize: '1rem' }}
                  >
                    {logDetail.Path || 'N/A'}
                  </Typography>
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Processed Date
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body1"
                    sx={{ color: 'text.primary', fontSize: '1rem' }}
                  >
                    {logDetail.FormattedProcessedDate || 'N/A'}
                  </Typography>
                }
              />
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Error Message
                  </Typography>
                }
                secondary={
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={logDetail.ErrorMessage || 'No error message available'}
                    InputProps={{
                      readOnly: true,
                      sx: { fontSize: '0.875rem' },
                    }}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                }
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Error Stack-trace
                  </Typography>
                }
                secondary={
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={logDetail.ErrorStack || 'No stack trace available'}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        maxHeight: '200px',
                        overflow: 'auto',
                      },
                    }}
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                }
              />
            </ListItem>
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} data-testid="status-dialog-close-button">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
